/**
 * AnalysisTemplateEditor — /agents/analysis-templates/new and /agents/analysis-templates/:id.
 * Demo page: `/form-layouts` (card form with a footer, tabs). Template: FormPage; the tab is kept in the URL.
 * The Template Assistant (floating panel) edits the same form.
 */
import React, { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { AlertTriangle, Copy } from 'lucide-react';
import {
  cloneAnalysisTemplateMutation,
  createAnalysisTemplateMutation,
  getAnalysisTemplateOptions,
  getAnalysisTemplateQueryKey,
  listAnalysisTemplatesQueryKey,
  updateAnalysisTemplateMutation,
} from '../../generated/api/@tanstack/react-query.gen.js';
import { zIntelAnalysisTemplateCreate } from '../../generated/api/zod.gen.js';
import type { IntelAnalysisTemplate } from '../../generated/api/types.gen.js';
import { Can } from '../../components/auth/Can.js';
import { FormPage } from '../../components/page/FormPage.js';
import { Button, Callout, Skeleton, Tabs, describeQueryError, useToast } from '../../components/ui';
import { Form, FormActions, useZodForm } from '../../components/form';
import { TemplateAssistant } from '../../components/intel/TemplateAssistant.js';
import { useTabParam } from '../../hooks/useTabParam.js';
import { ContractTab, DetailsTab, PLATFORMS, PromptTab, platformToCategory } from './AnalysisTemplateEditorTabs.js';

const LIST_PATH = '/agents/analysis-templates';

// The create schema, with the tag list edited as one comma-separated box (UI-only) and the category derived from
// the platform.
const editorSchema = zIntelAnalysisTemplateCreate
  .omit({ tags: true, suggestedRoleId: true })
  .extend({ tagsText: z.string().optional() });

const TAB_IDS = ['details', 'prompt', 'contract'] as const;
const TABS = [
  { id: 'details', label: 'Details' },
  { id: 'prompt', label: 'Prompt' },
  { id: 'contract', label: 'Contract' },
];

const CATEGORIES = Array.from(new Set(Object.values(platformToCategory)));

function parseTags(text: string | undefined): string[] {
  return (text ?? '')
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean);
}

interface TemplateFormProps {
  template: IntelAnalysisTemplate | undefined;
}

const TemplateForm: React.FC<TemplateFormProps> = ({ template }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { notify } = useToast();
  const isNew = template === undefined;
  const isSystem = template?.source === 'system';
  const [tab, setTab] = useTabParam(TAB_IDS, 'details');

  const form = useZodForm(editorSchema, {
    defaultValues: {
      platform: template?.platform ?? 'youtube',
      category: template?.category ?? 'social',
      name: template?.name ?? '',
      description: template?.description ?? '',
      promptTemplate: template?.instructions ?? '',
      tagsText: (template?.tags ?? []).join(', '),
    },
  });
  const platform = form.watch('platform');
  const promptLength = (form.watch('promptTemplate') ?? '').length;

  // The category follows the platform.
  useEffect(() => {
    form.setValue('category', platformToCategory[platform] ?? 'social');
  }, [form, platform]);

  const createMutation = useMutation(createAnalysisTemplateMutation());
  const updateMutation = useMutation(updateAnalysisTemplateMutation());
  const cloneMutation = useMutation({
    ...cloneAnalysisTemplateMutation(),
    onSuccess: (clone) => {
      queryClient.invalidateQueries({ queryKey: listAnalysisTemplatesQueryKey() });
      notify('Template cloned. You are editing the copy.');
      navigate(`${LIST_PATH}/${clone.id}`);
    },
    onError: () => notify('Could not clone the template.', 'danger'),
  });

  const save = async (values: z.output<typeof editorSchema>) => {
    const body = {
      platform: values.platform,
      category: platformToCategory[values.platform] ?? 'social',
      name: values.name,
      description: values.description,
      promptTemplate: values.promptTemplate,
      tags: parseTags(values.tagsText),
    };
    if (isNew) {
      const created = await createMutation.mutateAsync({ body });
      queryClient.invalidateQueries({ queryKey: listAnalysisTemplatesQueryKey() });
      notify('Template created.');
      navigate(`${LIST_PATH}/${created.id}`, { replace: true });
      return;
    }
    const id = String(template.id);
    await updateMutation.mutateAsync({ path: { id }, body });
    queryClient.invalidateQueries({ queryKey: listAnalysisTemplatesQueryKey() });
    queryClient.invalidateQueries({ queryKey: getAnalysisTemplateQueryKey({ path: { id } }) });
    notify('Template saved.');
  };

  const name = form.watch('name');
  const title = isNew ? 'New analysis template' : name || 'Edit analysis template';
  return (
    <>
      <FormPage
        title={title}
        subtitle="The prompt an agent runs on each item a source collects."
        breadcrumbs={[{ label: 'Knowledge' }, { label: 'Analysis templates', to: LIST_PATH }, { label: title }]}
        backTo={LIST_PATH}
      >
        <Form form={form} onSubmit={save} guardUnsaved={!isSystem} className="gap-5">
          {isSystem && (
            <Callout
              tone="warning"
              icon={AlertTriangle}
              title="System template"
              actions={
                <Can policy="CanManageAgents">
                  <Button
                    size="sm"
                    variant="secondary"
                    loading={cloneMutation.isPending}
                    onClick={() => cloneMutation.mutate({ path: { id: String(template.id) } })}
                  >
                    <Copy size={14} aria-hidden="true" /> Clone to customize
                  </Button>
                </Can>
              }
            >
              System templates cannot be edited. Clone it to make a copy for your organization.
            </Callout>
          )}

          <Tabs tabs={TABS} activeTab={tab} onChange={setTab} aria-label="Template sections" />

          {tab === 'details' && <DetailsTab platform={platform} disabled={isSystem} />}
          {tab === 'prompt' && <PromptTab length={promptLength} disabled={isSystem} />}
          {tab === 'contract' && <ContractTab template={template} />}

          {!isSystem && (
            <Can policy="CanManageAgents">
              <FormActions submitLabel={isNew ? 'Create template' : 'Save changes'} cancelTo={LIST_PATH} />
            </Can>
          )}
        </Form>
      </FormPage>

      {/* Assistant — manages its own open/closed state via its built-in button */}
      <TemplateAssistant
        {...(template ? { templateId: template.id } : {})}
        template={{
          name: form.watch('name'),
          description: form.watch('description'),
          platform: form.watch('platform') as IntelAnalysisTemplate['platform'],
          category: form.watch('category') as IntelAnalysisTemplate['category'],
          instructions: form.watch('promptTemplate'),
          tags: parseTags(form.watch('tagsText')),
        }}
        onTemplateUpdate={(updates) => {
          const set = { shouldDirty: true } as const;
          if (updates.name != null) form.setValue('name', updates.name, set);
          if (updates.description != null) form.setValue('description', updates.description, set);
          if (updates.platform != null) form.setValue('platform', updates.platform, set);
          if (updates.instructions != null) form.setValue('promptTemplate', updates.instructions, set);
          if (updates.tags != null) form.setValue('tagsText', updates.tags.join(', '), set);
        }}
        availablePlatforms={PLATFORMS}
        availableCategories={CATEGORIES}
        existingTemplates={[]}
      />
    </>
  );
};

export const AnalysisTemplateEditorPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  // `/new` is its own route without an `:id` param.
  const isNew = id === undefined || id === 'new';

  const query = useQuery({
    ...getAnalysisTemplateOptions({ path: { id: id ?? '' } }),
    enabled: !isNew && !!id,
  });

  if (isNew) return <TemplateForm key="new" template={undefined} />;

  if (query.isError) {
    return (
      <FormPage title="Analysis template" backTo={LIST_PATH}>
        <Callout
          tone="danger"
          title="Could not load this template"
          actions={
            <Button size="sm" variant="secondary" onClick={() => query.refetch()}>
              Retry
            </Button>
          }
        >
          {describeQueryError(query.error, 'this template')}
        </Callout>
      </FormPage>
    );
  }

  if (!query.data) {
    return (
      <FormPage title="Analysis template" backTo={LIST_PATH}>
        <div className="space-y-3">
          <Skeleton height={20} width="30%" />
          <Skeleton height={42} />
          <Skeleton height={120} />
        </div>
      </FormPage>
    );
  }

  return <TemplateForm key={query.data.id} template={query.data} />;
};
